import SportRoutingHelper from "./sport/SportRoutingHelper";

/**
 * SeoSchemaHelper - Helper to process SEO schema data
 * Enhanced with better validation and error handling
 */
const SeoSchemaHelper = {
    /**
    * This function encapsulates logic to ensure that the schema data
    * is in a valid JSON string format with improved validation
    *  */
    processSchemaData: function (schemaData) {
        try {
            // If schemaData is not provided, return a basic valid schema
            if(!schemaData){
                return JSON.stringify({ 
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "url": typeof window !== 'undefined' ? window.location.href : '',
                    "name": typeof window !== 'undefined' ? window.location.hostname : ''
                });
            }
            
            // If already a string, validate it's proper JSON
            if (typeof schemaData === 'string') {
                try {
                    JSON.parse(schemaData);
                    return schemaData;
                } catch (e) {
                    console.error('Invalid JSON schema data:', e);
                    return this.processSchemaData(null);
                }
            }
            
            // Convert object to JSON string
            return JSON.stringify(schemaData);
        } catch (error) {
            console.error('Error processing schema data:', error);
            return this.processSchemaData(null);
        }
    },
    /**
    * Generate a SportsEvent schema using dynamic sport event data.
    * Enhanced with better validation and comprehensive schema properties
    * @param {Object} sportData - Dynamic sport event data
    * @param {Array} customSchemas - Additional custom schemas to merge
    * @param {String} lang - Language code for the schema
    * @returns {String} - JSON stringified SportsEvent schema
    * */
    generateSportsEventSchema: function (sportData, customSchemas = [], lang) {
        try {
            // Validate required sport data
            if (!this.validateSportEventData(sportData)) {
                //try to get data from sport specific schema from url
                const path = window.location.pathname.replace(`/${lang}`, '') || '/';
                const sportPage = path.split('/')[2];
                const targetId = path.split('/')[3]; // Extract the target ID from the URL

                const routeObject = SportRoutingHelper.generateRouteObject(sportPage, targetId);
                if (!routeObject) {
                    console.warn('Could not generate route object for sport schema:', sportPage, targetId, path);
                    return this.processSchemaData(null);
                }
                // Try to generate schema from route object
                const fallbackSchema = this.generateSchemaFromRouteObject(routeObject, path);
                if (fallbackSchema) {
                    return fallbackSchema;
                }

            }

            // Build the enhanced SportsEvent schema using dynamic sport event data
            const schema = {
                "@context": "https://schema.org",
                "@type": "SportsEvent",
                "name": sportData.sportEventName, // Dynamic event name (e.g. "Team A vs Team B")
                "startDate": sportData.sportEventDateISO,
                "eventStatus": this.getEventStatus(sportData.eventStatus || 'scheduled'),
                "sport": sportData.sportName, // Sport type (e.g. "Basketball")
                "url": typeof window !== 'undefined' ? window.location.href : '',
                "description": sportData.description || `${sportData.sportEventName} - ${sportData.sportName}`,

                // Enhanced performer information (home team/main competitor)
                "performer": {
                    "@type": "SportsTeam",
                    "name": sportData.sportTeam1, // First team name
                    "sport": sportData.sportName, // Sport type
                    "memberOf": sportData.sportTournamentName ? {
                        "@type": "SportsOrganization",
                        "name": sportData.sportTournamentName // Tournament/organization name
                    } : undefined
                },

                // Enhanced competitor information
                "competitor": this.buildCompetitorsArray(sportData)
            };

            // Add optional properties if available
            if (sportData.venue || sportData.city || sportData.coutryName || sportData.sportTournamentName) {
                schema.location = this.buildLocationSchema(sportData);
            }

            if (sportData.sportTournamentName) {
                schema.organizer = {
                    "@type": "SportsOrganization",
                    "name": sportData.sportTournamentName
                };
            }

            if (sportData.sportEventEndDate) {
                schema.endDate = sportData.sportEventEndDate;
            }

            if (sportData.image) {
                schema.image = sportData.image;
            }

            // If additional custom schemas are provided, merge them in an array
            if (customSchemas && Array.isArray(customSchemas) && customSchemas.length > 0) {
                return JSON.stringify([schema, ...customSchemas]);
            }

            // Otherwise, return the enhanced schema as a JSON string
            return JSON.stringify(schema);
        } catch (error) {
            console.error('Error generating sports event schema:', error);
            return this.processSchemaData(null);
        }
    },

    /**
     * Validates sport event data for schema generation
     * @param {Object} sportData - Sport event data to validate
     * @returns {boolean} - Whether data is valid for schema generation
     */
    validateSportEventData: function (sportData) {
        if (!sportData || typeof sportData !== 'object') {
            return false;
        }

        // Required fields for a valid SportsEvent schema
        const requiredFields = ['sportEventName', 'sportEventDate', 'sportName'];

        return requiredFields.every(field => {
            const value = sportData[field];
            return value && typeof value === 'string' && value.trim().length > 0;
        });
    },

    /**
     * Gets proper event status URL for schema.org
     * @param {string} status - Event status
     * @returns {string} - Schema.org event status URL
     */
    getEventStatus: function (status) {
        const statusMap = {
            'scheduled': 'https://schema.org/EventScheduled',
            'cancelled': 'https://schema.org/EventCancelled',
            'postponed': 'https://schema.org/EventPostponed',
            'rescheduled': 'https://schema.org/EventRescheduled',
            'moved': 'https://schema.org/EventMovedOnline'
        };

        return statusMap[status?.toLowerCase()] || 'https://schema.org/EventScheduled';
    },

    /**
     * Builds location schema for the event
     * @param {Object} sportData - Sport event data
     * @returns {Object} - Location schema
     */
    buildLocationSchema: function (sportData) {
        const location = {
            "@type": "Place"
        };

        if (sportData.venue) {
            location.name = sportData.venue;
        }

        if (sportData.city || sportData.coutryName) {
            location.address = {
                "@type": "PostalAddress"
            };

            if (sportData.city) {
                location.address.addressLocality = sportData.city;
            }

            if (sportData.coutryName) {
                location.address.addressCountry = sportData.coutryName;
            }
        }

        return location;
    },

    /**
     * Builds competitors array for the event
     * @param {Object} sportData - Sport event data
     * @returns {Array} - Array of competitor schemas
     */
    buildCompetitorsArray: function (sportData) {
        const competitors = [];

        // Add second team as competitor
        if (sportData.sportTeam2) {
            competitors.push({
                "@type": "SportsTeam",
                "name": sportData.sportTeam2, // Second team name
                "sport": sportData.sportName, // Same sport type
                "memberOf": sportData.sportTournamentName ? {
                    "@type": "SportsOrganization",
                    "name": sportData.sportTournamentName // Same tournament/organization name
                } : undefined
            });
        }

        // Add first team as competitor if no second team (for consistency)
        if (!sportData.sportTeam2 && sportData.sportTeam1) {
            competitors.push({
                "@type": "SportsTeam",
                "name": sportData.sportTeam1,
                "sport": sportData.sportName,
                "memberOf": sportData.sportTournamentName ? {
                    "@type": "SportsOrganization",
                    "name": sportData.sportTournamentName
                } : undefined
            });
        }

        return competitors.length > 1 ? competitors : competitors[0] || null;
    },

    /**
     * Generates schema from route object when sport data validation fails
     * @param {Object} routeObject - Route object from SportRoutingHelper
     * @param {string} originalPath - Original URL path
     * @returns {string|null} - Generated schema or null
     */
    generateSchemaFromRouteObject: function (routeObject, originalPath) {
        if (!routeObject || !routeObject.path) {
            return null;
        }

        try {
            const baseSchema = {
                "@context": "https://schema.org",
                "url": typeof window !== 'undefined' ? window.location.href : ''
            };

            switch (routeObject.path) {
                case "/event-details":
                    return this.generateEventDetailsSchema(routeObject, baseSchema, originalPath);
                
                case "/pre-match":
                    return this.generatePreMatchSchema(routeObject, baseSchema);
                
                case "/sports":
                    return this.generatePreMatchSchema(routeObject, baseSchema);
                
                case "/live":
                    return this.generateLiveSchema(routeObject, baseSchema);
                
                case "/team-details":
                    return this.generateTeamDetailsSchema(routeObject, baseSchema, originalPath);
                
                case "/bet-race":
                    return this.generateBetRaceSchema(baseSchema);
                
                case "/bet-history":
                case "bet-history":
                    return this.generateBetHistorySchema(baseSchema);
                
                default:
                    return this.generateGenericSportsSchema(baseSchema);
            }
        } catch (error) {
            console.error('Error generating schema from route object:', error);
            return null;
        }
    },

    /**
     * Generates event details schema from route object
     * @param {Object} routeObject - Route object
     * @param {Object} baseSchema - Base schema object
     * @param {string} originalPath - Original URL path
     * @returns {string} - JSON schema
     */
    generateEventDetailsSchema: function (routeObject, baseSchema, originalPath) {
        const qs = routeObject.qs || {};
        const isLive = qs.live === "1";
        
        // Extract team names from URL if available
        const pathParts = originalPath.split('-');
        let teamNames = [];
        if (pathParts.length > 7) {
            // Get everything after the numeric parts as team names
            const numericParts = 7; // champ-event-country-sport-supertip-live-resultPage
            teamNames = pathParts.slice(numericParts).join('-').split('-vs-');
        }

        const schema = {
            ...baseSchema,
            "@type": "SportsEvent",
            "name": teamNames.length === 2 ? 
                `${this.capitalizeWords(teamNames[0])} vs ${this.capitalizeWords(teamNames[1])}` : 
                "Sports Event",
            "eventStatus": isLive ? 
                "https://schema.org/EventScheduled" : 
                "https://schema.org/EventScheduled",
            "sport": 'Sports',
            "description": `Sports event${teamNames.length === 2 ? ` between ${this.capitalizeWords(teamNames[0])} and ${this.capitalizeWords(teamNames[1])}` : ''}`,
            
            // Add performer and competitor if we have team names
            ...(teamNames.length === 2 && {
                "performer": {
                    "@type": "SportsTeam",
                    "name": this.capitalizeWords(teamNames[0]),
                    "sport": 'Sports'
                },
                "competitor": {
                    "@type": "SportsTeam", 
                    "name": this.capitalizeWords(teamNames[1]),
                    "sport": 'Sports'
                }
            })
        };

        // Add organizer if we have championship info
        if (qs.champ) {
            schema.organizer = {
                "@type": "SportsOrganization",
                "name": "Sports Championship"
            };
        }

        return JSON.stringify(schema);
    },

    /**
     * Generates live schema from route object
     * @param {Object} routeObject - Route object
     * @param {Object} baseSchema - Base schema object
     * @returns {string} - JSON schema
     */
    generateLiveSchema: function (routeObject, baseSchema) {
        const schema = {
            ...baseSchema,
            "@type": "SportsEvent",
            "name": "Live Sports Events",
            "description": "Live sports betting events and real-time odds",
            "eventStatus": "https://schema.org/EventScheduled",
            "sport": "Sports Betting"
        };

        return JSON.stringify(schema);
    },

    /**
     * Generates team details schema from route object
     * @param {Object} routeObject - Route object
     * @param {Object} baseSchema - Base schema object
     * @param {string} originalPath - Original URL path
     * @returns {string} - JSON schema
     */
    generateTeamDetailsSchema: function (routeObject, baseSchema, originalPath) {
        const qs = routeObject.qs || {};
        
        // Extract team and sport names from URL path
        // URL format: /team-details/eventId-competitorId-sportName-teamName
        const pathParts = originalPath.split('/');
        const teamDetailsPart = pathParts[pathParts.length - 1]; // Get the last part after team-details
        const parts = teamDetailsPart ? teamDetailsPart.split('-') : [];
        
        let sportName = '';
        let teamName = '';
        
        if (parts.length >= 4) {
            // Skip first two parts (eventId and competitorId), get sport and team name
            sportName = parts[2] || '';
            teamName = parts.slice(3).join(' '); // Join remaining parts as team name
        }

        const schema = {
            ...baseSchema,
            "@type": "SportsTeam",
            "name": teamName ? this.capitalizeWords(teamName) : "Sports Team",
            "sport": sportName ? this.capitalizeWords(sportName) : "Sports",
            "description": `Team details and statistics for ${teamName ? this.capitalizeWords(teamName) : 'sports team'}`,
            
            // Add additional properties if available
            ...(qs.eventId && {
                "identifier": qs.eventId
            }),
            
            ...(qs.competitorId && {
                "sameAs": `competitor-${qs.competitorId}`
            })
        };

        return JSON.stringify(schema);
    },

    /**
     * Generates bet history schema
     * @param {Object} baseSchema - Base schema object
     * @returns {string} - JSON schema
     */
    generateBetHistorySchema: function (baseSchema) {
        const schema = {
            ...baseSchema,
            "@type": "WebPage",
            "name": "Bet History",
            "description": "User betting history and past wagers"
        };

        return JSON.stringify(schema);
    },

    /**
     * Generates pre-match schema from route object
     * @param {Object} routeObject - Route object
     * @param {Object} baseSchema - Base schema object
     * @returns {string} - JSON schema
     */
    generatePreMatchSchema: function (routeObject, baseSchema) {
        const qs = routeObject.qs || {};
        
        let sportName = '';
        let championshipName = '';
        let description = 'Pre-match sports betting events and odds';
        
        
        // Check if we have query string parameters
        if (qs && Object.keys(qs).length > 0) {
            description = 'Sports betting events with filtered odds';
        }

        const schema = {
            ...baseSchema,
            "@type": "SportsEvent",
            "name": routeObject.path === "/sports" ? "Sports Events" : "Pre-Match Sports Events",
            "description": description,
            "eventStatus": "https://schema.org/EventScheduled",
            "sport": sportName || "Sports Betting"
        };

        // Add organizer if we have championship info
        if (championshipName) {
            schema.organizer = {
                "@type": "SportsOrganization",
                "name": championshipName
            };
        }

        return JSON.stringify(schema);
    },

    /**
     * Generates bet race schema
     * @param {Object} baseSchema - Base schema object
     * @returns {string} - JSON schema
     */
    generateBetRaceSchema: function (baseSchema) {
        const schema = {
            ...baseSchema,
            "@type": "SportsEvent",
            "name": "Bet Race",
            "description": "Live betting race events and competitions",
            "eventStatus": "https://schema.org/EventScheduled",
            "sport": "Sports Betting"
        };

        return JSON.stringify(schema);
    },

    /**
     * Generates generic sports schema
     * @param {Object} baseSchema - Base schema object
     * @returns {string} - JSON schema
     */
    generateGenericSportsSchema: function (baseSchema) {
        const schema = {
            ...baseSchema,
            "@type": "WebPage",
            "name": "Sports Betting",
            "description": "Sports betting platform with live events and odds"
        };

        return JSON.stringify(schema);
    },

    /**
     * Capitalizes words and replaces hyphens with spaces
     * @param {string} str - String to capitalize
     * @returns {string} - Capitalized string
     */
    capitalizeWords: function (str) {
        if (!str) return '';
        return str.replace(/-/g, ' ')
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ');
    },

    /**
* Formats date for schema.org format (ISO 8601)
* @param {string|Date} date - Date to format
* @returns {string|null} - Formatted date or null if invalid
*/
    formatDateForSchema: function (date) {
        if (!date) return null;
        const currentYear = new Date().getFullYear();
        let dateObj;

        // If the date is a string, trim it and try to parse
        if (typeof date === 'string') {
            const trimmedDate = date.trim();
            dateObj = new Date(`${trimmedDate}, ${currentYear}`);

            // If the date has already passed this year, use next year
            if (dateObj.getTime() < Date.now()) {
                dateObj = new Date(`${trimmedDate}, ${currentYear + 1}`);
            }
        } else if (date instanceof Date) {
            dateObj = date;
        }

        // Format the date as ISO 8601
        return dateObj.toISOString();
    }
};

export default SeoSchemaHelper;
